import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

type Closer = () => void

interface OverlayBackApi {
  /** Кладёт оверлей в стек. Возвращает функцию, снимающую его оттуда. */
  register: (closer: Closer) => () => void
}

/**
 * Стек открытых оверлеев. Нужен именно стек, а не один слот: поверх шторки
 * рецепта открывается шторка с ценой продукта, и после её закрытия «Назад»
 * должен снова закрывать рецепт, а не уводить со страницы.
 */
export const OverlayBackContext = createContext<OverlayBackApi>({
  register: () => () => {},
})

/**
 * Держится на странице. Отдаёт обработчик для кнопки «Назад» и сам слушает
 * Escape — централизованно, чтобы на одно нажатие закрывался только верхний
 * оверлей, а не все сразу.
 */
export const useOverlayBackStack = () => {
  const stack = useRef<Closer[]>([])
  // Состояние нужно только для того, чтобы кнопка «Назад» получила обработчик,
  // когда оверлей открыт, и снова осталась без него, когда закрыт.
  const [hasOverlay, setHasOverlay] = useState(false)

  const closeTop = useCallback(() => {
    stack.current[stack.current.length - 1]?.()
  }, [])

  const api = useMemo<OverlayBackApi>(
    () => ({
      register: closer => {
        stack.current = [...stack.current, closer]
        setHasOverlay(true)
        return () => {
          stack.current = stack.current.filter(one => one !== closer)
          setHasOverlay(stack.current.length > 0)
        }
      },
    }),
    []
  )

  useEffect(() => {
    if (!hasOverlay) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeTop()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [hasOverlay, closeTop])

  return { api, onBack: hasOverlay ? closeTop : undefined }
}

/**
 * Пока оверлей открыт: кнопка «Назад» и Escape закрывают его, а страница под
 * ним не прокручивается. Оверлеи рендерятся по условию, поэтому «смонтирован»
 * здесь равно «открыт».
 *
 * Регистрируем стабильную обёртку над ссылкой на свежий обработчик: если
 * записывать сам обработчик, каждый его новый идентификатор перерегистрировал
 * бы оверлей в стеке.
 */
export const useOverlayBack = (close: () => void) => {
  const { register } = useContext(OverlayBackContext)
  const latest = useRef(close)

  // Присваиваем в эффекте, а не в теле: в concurrent-рендере тело может
  // отработать для рендера, который так и не будет показан.
  useEffect(() => {
    latest.current = close
  }, [close])

  useEffect(() => {
    const unregister = register(() => latest.current())

    // Прокрутку страницы под оверлеем запрещаем: иначе после закрытия человек
    // оказывается не там, где был, а в Telegram на iOS вертикальный свайп
    // тянет вебвью к закрытию мини-аппа. Вложенный оверлей запомнит уже
    // запрещённую прокрутку и вернёт её такой же — снимет запрет только
    // самый нижний.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      unregister()
      document.body.style.overflow = previousOverflow
    }
  }, [register])
}
